import { Account } from "../../model";

export enum Allocation {
	Common = "COMMON_ALLOCATION",
	LumpSum = "LUMPSUM_ALLOCATION",
	Equalization = "EQUALIZATION_ALLOCATION",
	Participation = "PARTICIPATION",
	OfCompensation = "ATTRIBUTION_OF_COMPENSATION",
	Other = "OTHER_ALLOCATION"
}

/** Libellés des types de dotations */
export const allocationLabels: AllocationLabels = {
	/** Dotation */
	[Allocation.Common]: "Dotation",
	/** Dotation forfaitaire */
	[Allocation.LumpSum]: "Dotation forfaitaire",
	/** Dotation péréquation */
	[Allocation.Equalization]: "Dotation péréquation",
	/** Participation */
	[Allocation.Participation]: "Participation",
	/** Attribution de compensation */
	[Allocation.OfCompensation]: "Attribution de compensation",
	/** Autres dotations */
	[Allocation.Other]: "Autres dotations"
}

/** Numéros des comptes de dotations */
export const allocationNumbers: AllocationNumbers = {
	/** Toutes les dotations */
	[Allocation.Common]: ["74"],
	/** Dotation forfaitaire */
	[Allocation.LumpSum]: ["7411"],
	/** Dotation péréquation */
	[Allocation.Equalization]: ["7412"],
	/** Participation */
	[Allocation.Participation]: ["747"],
	/** Attribution de compensation */
	[Allocation.OfCompensation]: ["748"]
}

/**
 * Vérifie si le compte passé en paramètre est une dotation.
 * Si le compte ou son numéro ne sont pas définis, retourn `false`.
 * @param account
 */
export function accountIsAllocation(account: Account): boolean {
	if (!account || !account.number) return false
	return allocationNumbers[Allocation.Common].some(n =>
		account.number.startsWith(n)
	)
}

/**
 * Vérifie si le compte passé en paramètre est une dotation forfaitaire.
 * Si le compte ou son numéro ne sont pas définis, retourn `false`.
 * @param account
 */
export function accountIsLumpSumAllocation(account: Account): boolean {
	if (!account || !account.number) return false
	return allocationNumbers[Allocation.LumpSum].some(n =>
		account.number.startsWith(n)
	)
}

/**
 * Vérifie si le compte passé en paramètre est une dotation péréquation.
 * Si le compte ou son numéro ne sont pas définis, retourn `false`.
 * @param account
 */
export function accountIsEqualizationAllocation(account: Account): boolean {
	if (!account || !account.number) return false
	return allocationNumbers[Allocation.Equalization].some(n =>
		account.number.startsWith(n)
	)
}

/**
 * Vérifie si le compte passé en paramètre est une participation.
 * Si le compte ou son numéro ne sont pas définis, retourn `false`.
 * @param account
 */
export function accountIsParticipation(account: Account): boolean {
	if (!account || !account.number) return false
	return allocationNumbers[Allocation.Participation].some(n =>
		account.number.startsWith(n)
	)
}

/**
 * Vérifie si le compte passé en paramètre est une attribution de compensation.
 * Si le compte ou son numéro ne sont pas définis, retourn `false`.
 * @param account
 */
export function accountIsAttributionOfCompensation(account: Account): boolean {
	if (!account || !account.number) return false
	return allocationNumbers[Allocation.OfCompensation].some(n =>
		account.number.startsWith(n)
	)
}

/**
 * Vérifie si le compte passé en paramètre est une autre dotation.
 * Un compte est une autre dotation s'il est une dotation, mais qu'il n'est pas
 * une dotation forfaitaire, ni une dotation péréquation, ni une participation,
 * ni une attribution de compensation.
 * @param account
 */
export function accountIsOtherAllocation(account: Account): boolean {
	return accountIsAllocation(account)
	&& !accountIsLumpSumAllocation(account)
	&& !accountIsEqualizationAllocation(account)
	&& !accountIsParticipation(account)
	&& !accountIsAttributionOfCompensation(account)
}

/**
 * Vérifie si le compte passé en paramètre est du type de dotation passé en
 * paramètre.
 * Si le compte ou son numéro ne sont pas définis, retourn `false`.
 * @param account
 * @param allocation
 */
export function accountIsSpecificAllocation(
	account: Account,
	allocation: Allocation
): boolean {
	if (!account || !account.number) return false
	if (allocation === Allocation.Other) return accountIsOtherAllocation(account)

	return allocationNumbers[allocation].some(n => account.number.startsWith(n))
}

interface AllocationLabels {
	[key: string]: string
}

interface AllocationNumbers {
	[key: string]: string[]
}


// WEBPACK FOOTER //
// ./src/domain/sfp/allocation-domain.ts